<!-- src/components/MealsCalendarView.vue -->
<script setup lang="ts">
import { ref, computed } from 'vue'
import { useMutation, useQuery } from '@vue/apollo-composable'
import { UPDATE_MEAL } from '../graphql/mutations'
import { GET_MEALS_BY_DATE_RANGE } from '../graphql/queries'
import { showError, showSuccess } from '../utils/errorHandling'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)

const emit = defineEmits<{
  'create-meal-on-date': [date: string]
  'edit-meal': [meal: any]
}>()

const currentMonth = ref(dayjs().startOf('month'))
const draggingMeal = ref<any>(null)
const dragOverKey = ref<string | null>(null)

const WEEK_DAYS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN']

const { mutate: updateMeal } = useMutation(UPDATE_MEAL)

// The Monday-to-Sunday grid bounds for the currently displayed month, including
// the leading/trailing days from adjacent months shown to fill the grid.
const gridBounds = computed(() => {
  const start = currentMonth.value.startOf('month')
  const daysInMonth = start.daysInMonth()
  const startDow = (start.day() + 6) % 7
  const endDate = start.date(daysInMonth)
  const endDow = (endDate.day() + 6) % 7
  return {
    first: start.subtract(startDow, 'day'),
    last: endDate.add(6 - endDow, 'day'),
  }
})

const { result: mealsResult, refetch: refetchMeals } = useQuery(GET_MEALS_BY_DATE_RANGE, () => ({
  start_date: gridBounds.value.first.format('DD/MM/YYYY'),
  end_date: gridBounds.value.last.format('DD/MM/YYYY'),
  show_active: false,
}))

const meals = computed(() => mealsResult.value?.mealsByDateRange || [])

const mealsByDate = computed(() => {
  const map: Record<string, any[]> = {}
  for (const meal of meals.value) {
    if (!meal.scheduled_date) continue
    const dates = meal.scheduled_dates?.length ? meal.scheduled_dates : [meal.scheduled_date]
    for (const d of dates) {
      const key = dayjs.utc(d).format('YYYY-MM-DD')
      if (!map[key]) map[key] = []
      map[key].push(meal)
    }
  }
  return map
})

const calendarDays = computed(() => {
  const todayKey = dayjs().format('YYYY-MM-DD')
  const { first, last } = gridBounds.value

  const days: { date: dayjs.Dayjs; utcKey: string; isCurrentMonth: boolean; isToday: boolean; meals: any[] }[] = []
  let cursor = first
  while (!cursor.isAfter(last, 'day')) {
    const utcKey = cursor.format('YYYY-MM-DD')
    const isCurrentMonth = cursor.isSame(currentMonth.value, 'month')
    days.push({ date: cursor, utcKey, isCurrentMonth, isToday: utcKey === todayKey, meals: mealsByDate.value[utcKey] || [] })
    cursor = cursor.add(1, 'day')
  }

  return days
})

const isCurrentMonthNow = computed(() => currentMonth.value.isSame(dayjs().startOf('month')))

const prevMonth = () => { currentMonth.value = currentMonth.value.subtract(1, 'month') }
const nextMonth = () => { currentMonth.value = currentMonth.value.add(1, 'month') }
const goToToday  = () => { currentMonth.value = dayjs().startOf('month') }

// ── Drag & drop ──────────────────────────────────────────────────────────────
const onDragStart = (e: DragEvent, meal: any) => {
  draggingMeal.value = meal
  e.dataTransfer!.effectAllowed = 'move'
}

const onDragOver = (e: DragEvent, utcKey: string, isCurrentMonth: boolean) => {
  if (!isCurrentMonth) return
  e.preventDefault()
  e.dataTransfer!.dropEffect = 'move'
  dragOverKey.value = utcKey
}

const onDragLeave = (e: DragEvent) => {
  const related = e.relatedTarget as HTMLElement | null
  if (!related || !(e.currentTarget as HTMLElement).contains(related)) {
    dragOverKey.value = null
  }
}

const onDragEnd = () => {
  draggingMeal.value = null
  dragOverKey.value = null
}

const onDrop = async (utcKey: string, isCurrentMonth: boolean) => {
  dragOverKey.value = null
  const meal = draggingMeal.value
  draggingMeal.value = null
  if (!meal || !isCurrentMonth) return

  const currentKey = dayjs.utc(meal.scheduled_date).format('YYYY-MM-DD')
  if (currentKey === utcKey) return

  try {
    const newScheduledDate = `${utcKey}T00:00:00.000Z`

    // Shift every scheduled date by the same delta so multi-day meals move as a block.
    // Falls back to [scheduled_date] for single-day meals.
    const deltaDays = dayjs(utcKey).diff(dayjs(currentKey), 'day')
    const allDates = meal.scheduled_dates?.length ? meal.scheduled_dates : [meal.scheduled_date]
    const newScheduledDates = allDates.map((d: string) => {
      const shifted = dayjs.utc(d).add(deltaDays, 'day').format('YYYY-MM-DD')
      return `${shifted}T00:00:00.000Z`
    })

    await updateMeal({
      id: meal.id,
      title: meal.title,
      description: meal.description ?? null,
      recipe_id: meal.recipe_id,
      type_id: meal.type_id,
      scheduled_date: newScheduledDate,
      scheduled_dates: newScheduledDates,
      portions_days: newScheduledDates.length,
      portions_mode: meal.portions_mode ?? undefined,
    })
    showSuccess('Meal rescheduled!')
    refetchMeals()
  } catch (e) {
    showError(e, 'reschedule meal')
  }
}
</script>

<template>
  <div class="cal-card card-elevated overflow-hidden">

    <!-- Month navigation -->
    <div class="flex items-center justify-between px-4 py-3 border-b border-gray-100 flex-shrink-0">
      <button @click="prevMonth" class="cal-nav-btn">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
        </svg>
      </button>

      <div class="flex items-center gap-3">
        <h3 class="text-base font-bold text-gray-900">{{ currentMonth.format('MMMM YYYY') }}</h3>
        <button v-if="!isCurrentMonthNow" @click="goToToday" class="cal-today-btn">Today</button>
      </div>

      <button @click="nextMonth" class="cal-nav-btn">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
        </svg>
      </button>
    </div>

    <!-- Legend -->
    <div class="cal-legend flex-shrink-0">
      <span class="cal-legend-label">Key:</span>
      <span class="cal-legend-item cal-legend-item--leftovers">
        <svg class="cal-legend-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M15.362 5.214A8.252 8.252 0 0112 21 8.25 8.25 0 016.038 7.048 8.287 8.287 0 009 9.6a8.983 8.983 0 013.361-6.867 8.21 8.21 0 003 2.48z" />
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M12 18a3.75 3.75 0 00.495-7.467 5.99 5.99 0 00-1.925 3.546 5.974 5.974 0 01-2.133-1.001A3.75 3.75 0 0012 18z" />
        </svg>
        Cooked Once
      </span>
      <span class="cal-legend-item cal-legend-item--repeat">
        <svg class="cal-legend-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
        </svg>
        Cook fresh each day
      </span>
    </div>

    <!-- Day-of-week headers -->
    <div class="cal-dow-row flex-shrink-0">
      <div v-for="dow in WEEK_DAYS" :key="dow" class="cal-dow-cell">{{ dow }}</div>
    </div>

    <!-- Scrollable grid area -->
    <div class="cal-grid-area">
      <div class="cal-grid">
        <div
          v-for="day in calendarDays"
          :key="day.utcKey"
          class="cal-cell"
          :class="{
            'cal-cell--other-month': !day.isCurrentMonth,
            'cal-cell--has-meals':   day.isCurrentMonth && !day.isToday && day.meals.length > 0,
            'cal-cell--today':        day.isToday,
            'cal-cell--active':       day.isCurrentMonth,
            'cal-cell--drag-over':    dragOverKey === day.utcKey,
          }"
          @dragover="onDragOver($event, day.utcKey, day.isCurrentMonth)"
          @dragleave="onDragLeave($event)"
          @drop.prevent="onDrop(day.utcKey, day.isCurrentMonth)"
        >
          <span class="cal-day-num">{{ day.date.date() }}</span>

          <!-- Empty cell: centred "Plan meal" button -->
          <div v-if="day.isCurrentMonth && day.meals.length === 0" class="cal-add-hint">
            <button class="cal-plan-btn" @click.stop="emit('create-meal-on-date', day.utcKey)">
              Plan meal
            </button>
          </div>

          <!-- Cells with meals -->
          <div v-if="day.meals.length > 0" class="cal-meals">
            <div
              v-for="meal in day.meals.slice(0, 2)"
              :key="meal.id"
              class="cal-meal-pill"
              :class="{ 'cal-meal-pill--dragging': draggingMeal?.id === meal.id }"
              draggable="true"
              @dragstart="onDragStart($event, meal)"
              @dragend="onDragEnd"
              @click.stop="emit('edit-meal', meal)"
            >
              {{ meal.title }}
              <span v-if="(meal.portions_days ?? 1) > 1"
                class="cal-multiday-badge"
                :class="meal.portions_mode === 'repeat' ? 'cal-multiday-badge--repeat' : 'cal-multiday-badge--leftovers'">
                <svg class="cal-multiday-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path v-if="meal.portions_mode === 'repeat'" stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5"
                    d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
                  <template v-else>
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5"
                      d="M15.362 5.214A8.252 8.252 0 0112 21 8.25 8.25 0 016.038 7.048 8.287 8.287 0 009 9.6a8.983 8.983 0 013.361-6.867 8.21 8.21 0 003 2.48z" />
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5"
                      d="M12 18a3.75 3.75 0 00.495-7.467 5.99 5.99 0 00-1.925 3.546 5.974 5.974 0 01-2.133-1.001A3.75 3.75 0 0012 18z" />
                  </template>
                </svg>{{ meal.portions_days }}d</span>
            </div>
            <span v-if="day.meals.length > 2" class="cal-overflow">
              +{{ day.meals.length - 2 }} Meals
            </span>
          </div>

          <!-- "+" button for cells that already have meals -->
          <button
            v-if="day.isCurrentMonth && day.meals.length > 0"
            class="cal-add-more"
            @click.stop="emit('create-meal-on-date', day.utcKey)"
          >+</button>
        </div>
      </div>
    </div>

  </div>
</template>

<style scoped>
/* ── Card: flex column bounded by viewport ──────────────────────────────── */
.cal-card {
  display: flex;
  flex-direction: column;
  /* 260px ≈ nav + page-header + gaps above the calendar */
  height: calc(100dvh - 260px);
  min-height: 420px;
}

/* ── Day-of-week header row ─────────────────────────────────────────────── */
.cal-dow-row {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  background-color: #F8FAFC;
  border-bottom: 1px solid #F1F5F9;
}

.cal-dow-cell {
  text-align: center;
  padding: 8px 0;
  font-size: 11px;
  font-weight: 700;
  color: #94A3B8;
  letter-spacing: 0.04em;
}

/* ── Grid area: fills remaining card height, scrolls if cells overflow ──── */
.cal-grid-area {
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

/* ── Grid: rows are at least 140 px; grow to fill available height ───────── */
.cal-grid {
  display: grid;
  height: 100%;
  grid-template-columns: repeat(7, 1fr);
  grid-auto-rows: 1fr;
  gap: 4px;
  padding: 4px;
  background-color: #F6F9F6;
}

/* ── Cell base ──────────────────────────────────────────────────────────── */
.cal-cell {
  position: relative;
  border-radius: 6px;
  padding: 8px;
  background-color: white;
  cursor: default;
  overflow: hidden;
  transition: background-color 150ms ease;
}

.cal-cell--active {
  cursor: default;
}

.cal-cell--other-month {
  background-color: #FAFAFA;
  opacity: 0.5;
}

/* ── Cell state: has meals ──────────────────────────────────────────────── */
.cal-cell--has-meals {
  background-color: #EAF1EB;
}

/* ── Cell state: today ──────────────────────────────────────────────────── */
.cal-cell--today {
  background-color: #4F8646E5;
}

/* ── Hover: active (current-month) cells only ───────────────────────────── */
.cal-cell--active:hover {
  background-color: #4F8646E5;
}

/* ── Day number ─────────────────────────────────────────────────────────── */
.cal-day-num {
  display: block;
  text-align: center;
  font-size: 14px;
  font-weight: 400;
  color: #0F172A;
  margin-bottom: 4px;
  line-height: 1.4;
}

.cal-cell--other-month .cal-day-num {
  color: #CBD5E1;
}

.cal-cell--today .cal-day-num,
.cal-cell--active:hover .cal-day-num {
  color: white;
  font-weight: 600;
}

/* ── Drag-over target ───────────────────────────────────────────────────── */
.cal-cell--drag-over {
  outline: 2px dashed rgba(79, 134, 70, 0.8);
  outline-offset: -3px;
  background-color: rgba(79, 134, 70, 0.12) !important;
}

.cal-cell--drag-over .cal-day-num {
  color: #0F172A !important;
  font-weight: 600;
}

/* ── Dragged pill ───────────────────────────────────────────────────────── */
.cal-meal-pill--dragging {
  opacity: 0.4;
}

/* ── "+" button on cells with meals ────────────────────────────────────── */
.cal-add-more {
  position: absolute;
  bottom: 5px;
  right: 5px;
  width: 20px;
  height: 20px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.25);
  border: none;
  color: white;
  font-size: 16px;
  font-weight: 700;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: 0;
  transition: opacity 150ms ease;
  padding: 0;
}

.cal-cell--active:hover .cal-add-more,
.cal-cell--today .cal-add-more {
  opacity: 1;
}

/* ── Add hint (empty cells) ─────────────────────────────────────────────── */
.cal-add-hint {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 150ms ease;
}

.cal-plan-btn {
  display: inline-block;
  background: rgba(255, 255, 255, 0.25);
  border: none;
  color: white;
  font-size: 14px;
  font-weight: 700;
  padding: 6px 14px;
  border-radius: 6px;
  letter-spacing: 0.02em;
  white-space: nowrap;
  cursor: pointer;
}

.cal-cell--active:hover .cal-add-hint {
  opacity: 1;
}

/* ── Meal pills container ───────────────────────────────────────────────── */
.cal-meals {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

/* ── Single meal pill ───────────────────────────────────────────────────── */
.cal-meal-pill {
  display: block;
  background-color: white;
  border-radius: 4px;
  padding: 2px 6px;
  font-size: 14px;
  font-weight: 700;
  color: #0F172A;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
  transition: opacity 150ms ease;
}

.cal-meal-pill:hover {
  opacity: 0.85;
}

.cal-multiday-badge {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  margin-left: 4px;
  font-size: 9px;
  font-weight: 700;
  border-radius: 3px;
  padding: 0 3px;
  vertical-align: middle;
  line-height: 14px;
}
.cal-multiday-badge--leftovers {
  background: var(--color-primary-50, #EDF3EC);
  color: var(--color-primary-700, #3F4A3C);
}
.cal-multiday-badge--repeat {
  background: #EFF6FF;
  color: #1D4ED8;
}
.cal-multiday-icon {
  width: 9px;
  height: 9px;
  flex-shrink: 0;
}

/* ── Legend strip ───────────────────────────────────────────────────────── */
.cal-legend {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 4px 12px;
  background: #F8FAFC;
  border-bottom: 1px solid #F1F5F9;
}
.cal-legend-label {
  font-size: 10px;
  font-weight: 600;
  color: #94A3B8;
  letter-spacing: 0.04em;
  margin-right: 2px;
}
.cal-legend-item {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 10px;
  font-weight: 600;
  padding: 1px 5px;
  border-radius: 3px;
}
.cal-legend-item--leftovers {
  background: var(--color-primary-50, #EDF3EC);
  color: var(--color-primary-700, #3F4A3C);
}
.cal-legend-item--repeat {
  background: #EFF6FF;
  color: #1D4ED8;
}
.cal-legend-icon {
  width: 10px;
  height: 10px;
  flex-shrink: 0;
}

/* ── Overflow count ─────────────────────────────────────────────────────── */
.cal-overflow {
  font-size: 10px;
  font-weight: 700;
  color: #3F4A3C;
  padding: 1px 4px;
}

.cal-cell--today .cal-overflow,
.cal-cell--active:hover .cal-overflow {
  color: white;
}

/* ── Navigation buttons ─────────────────────────────────────────────────── */
.cal-nav-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  background: transparent;
  border: none;
  color: #64748B;
  cursor: pointer;
  padding: 0;
  transition: background-color 150ms ease, color 150ms ease;
}

.cal-nav-btn:hover {
  background-color: #F1F5F9;
  color: #0F172A;
}

/* ── Today shortcut button ──────────────────────────────────────────────── */
.cal-today-btn {
  font-size: 12px;
  font-weight: 600;
  color: #4F8646;
  background: transparent;
  border: none;
  border-radius: 4px;
  padding: 2px 8px;
  cursor: pointer;
  transition: background-color 150ms ease;
}

.cal-today-btn:hover {
  background-color: #EAF1EB;
}
</style>
